import React, {createContext, useContext, useEffect, useMemo, useState} from 'react';
import {useAuthState} from '../../../shared/services/firebase';
import PropTypes from "prop-types";
import {getTenantErrorMessage, tenantsApi} from '../services/tenantsApi';

const TenantsContext = createContext();

export const useTenants = () => useContext(TenantsContext);

export const TenantsProvider = ({children}) => {
  const {user} = useAuthState();
  const [currentTenant, setCurrentTenant] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTenant = async () => {
      // If no user is logged in, reset tenant
      if (!user) {
        setCurrentTenant(null);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const tenant = await tenantsApi.getUserTenantContext(user);

        if (tenant) {
          console.log('User tenant path:', tenant.path);

          // Set tenant state immediately so EdgesContext can start fetching edges
          // Custom claims will be set in background to refresh token
          setCurrentTenant(tenant);
          const claimResult = await tenantsApi.setCurrentTenantClaim(tenant.path);

          if (claimResult.ok) {
            console.log('Current tenant set in custom claims:', tenant.id);
          } else {
            console.error('Failed to set current tenant in custom claims:', getTenantErrorMessage(claimResult.error));
          }
        } else {
          // User has no tenant membership
          console.log('No tenant membership found for user:', user.email);
          setCurrentTenant(null);
        }
      } catch (error) {
        console.error('Error loading tenant context:', getTenantErrorMessage(error), error);
        setCurrentTenant(null);
      } finally {
        setLoading(false);
      }
    };

    fetchTenant();
  }, [user]);

  const value = useMemo(() => ({currentTenant, loading}), [currentTenant, loading]);

  return (
    <TenantsContext.Provider value={value}>
      {children}
    </TenantsContext.Provider>
  );
};

TenantsProvider.propTypes = {
  children: PropTypes.node.isRequired
};